import { useEffect, useRef, useState } from "react";
import api, { apiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trophy, Camera, Upload, Star, UserRound, Loader2, FileDown, Image as ImageIcon, Quote, TrendingUp, TrendingDown, Minus, History } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DeltaBadge = ({ value, label }) => {
  if (value === null || value === undefined) {
    return <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">İlk KTT</span>;
  }
  const up = value > 0, down = value < 0;
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  const cls = up ? "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40"
    : down ? "text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40"
    : "text-muted-foreground bg-muted";
  const sign = up ? "+" : "";
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold ${cls}`}>
      <Icon className="h-3 w-3" />{sign}{value} {label}
    </span>
  );
};

const ACCENTS = {
  "5": { grad: "from-amber-400 via-amber-500 to-orange-500", ring: "ring-amber-400", text: "text-amber-600 dark:text-amber-400", soft: "bg-amber-50 dark:bg-amber-950/30" },
  "6": { grad: "from-sky-400 via-sky-500 to-blue-600", ring: "ring-sky-400", text: "text-sky-600 dark:text-sky-400", soft: "bg-sky-50 dark:bg-sky-950/30" },
  "7": { grad: "from-violet-400 via-violet-500 to-purple-600", ring: "ring-violet-400", text: "text-violet-600 dark:text-violet-400", soft: "bg-violet-50 dark:bg-violet-950/30" },
  "8": { grad: "from-emerald-400 via-emerald-500 to-teal-600", ring: "ring-emerald-400", text: "text-emerald-600 dark:text-emerald-400", soft: "bg-emerald-50 dark:bg-emerald-950/30" },
};
const accentFor = (g) => ACCENTS[String(g)] || { grad: "from-slate-400 to-slate-600", ring: "ring-slate-400", text: "text-slate-600", soft: "bg-slate-50 dark:bg-slate-900/40" };

const fileToSquareJpeg = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const S = 420;
      const side = Math.min(img.width, img.height);
      const canvas = document.createElement("canvas");
      canvas.width = S; canvas.height = S;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, S, S);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    img.src = String(reader.result);
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export default function StudentOfWeek({ isAdmin, compact, examType = "KTT" }) {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [examList, setExamList] = useState([]);
  const [selExam, setSelExam] = useState("");
  const [busy, setBusy] = useState("");
  const [exporting, setExporting] = useState("");
  const inputs = useRef({});

  const load = (exId) => api.get("/student-of-week" + (exId ? `?exam_id=${exId}&exam_type=${examType}` : `?exam_type=${examType}`)).then((r) => setData(r.data)).catch(() => setData({ exam: null, winners: [] }));
  useEffect(() => { load(selExam); }, [selExam, compact, examType]);
  useEffect(() => {
    if (!compact) api.get("/student-of-week/history").then((r) => { const w = r.data.weeks || []; setHistory(w); setExamList(w.map((x) => x.exam)); }).catch(() => setHistory([]));
  }, [compact]);

  const onPhoto = async (winner, ev) => {
    const f = ev.target.files?.[0];
    ev.target.value = "";
    if (!f) return;
    if (!winner.student_id) { toast.error("Bu öğrenci manuel girilmiş, fotoğraf eklenemiyor"); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Fotoğraf 8 MB'tan küçük olmalı"); return; }
    try {
      setBusy(winner.grade);
      const photo_url = await fileToSquareJpeg(f);
      await api.put(`/students/${winner.student_id}/photo`, { photo_url });
      setData((d) => ({ ...d, winners: d.winners.map((w) => w.grade === winner.grade ? { ...w, photo_url } : w) }));
      toast.success(`${winner.student_name} fotoğrafı güncellendi`);
    } catch (e) { toast.error(apiError(e, "Fotoğraf yüklenemedi")); }
    finally { setBusy(""); }
  };

  const removePhoto = async (winner) => {
    try {
      await api.put(`/students/${winner.student_id}/photo`, { photo_url: "" });
      setData((d) => ({ ...d, winners: d.winners.map((w) => w.grade === winner.grade ? { ...w, photo_url: "" } : w) }));
      toast.success("Fotoğraf kaldırıldı");
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadPdf = async () => {
    const q = selExam ? `?exam_id=${selExam}` : "";
    try { setExporting("pdf"); await downloadFile(`/student-of-week/poster.pdf${q}`, "haftanin_ogrencileri.pdf"); toast.success("Afiş (PDF) indirildi"); }
    catch (e) { toast.error(apiError(e, "Afiş oluşturulamadı")); }
    finally { setExporting(""); }
  };

  const downloadJpeg = async () => {
    const q = selExam ? `?exam_id=${selExam}` : "";
    try { setExporting("jpg"); await downloadFile(`/student-of-week/poster.jpg${q}`, "haftanin_ogrencileri.jpg"); toast.success("Afiş (JPEG) indirildi"); }
    catch (e) { toast.error(apiError(e, "Görsel oluşturulamadı")); }
    finally { setExporting(""); }
  };

  const downloadHistoryPdf = async () => {
    try { setExporting("hist"); await downloadFile(`/student-of-week/history.pdf`, "gecmis_haftalarin_sampiyonlari.pdf"); toast.success("Geçmiş şampiyonlar PDF indirildi"); }
    catch (e) { toast.error(apiError(e, "PDF oluşturulamadı")); }
    finally { setExporting(""); }
  };

  if (!data) return compact ? null : <div className="text-muted-foreground">Yükleniyor...</div>;

  if (compact) {
    if (!data.exam || data.winners.length === 0) return null;
    return (
      <div className="mb-5 rounded-2xl border border-amber-500/20 bg-gradient-to-r from-amber-500/10 via-primary/5 to-transparent p-4" data-testid="sow-strip">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 grid place-items-center shrink-0">
            <Trophy className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-sm leading-tight">Haftanın Öğrencileri</h3>
            <p className="text-[11px] text-muted-foreground truncate">{data.exam.name}{data.exam.date ? ` · ${data.exam.date}` : ""}</p>
          </div>
          <div className="sow-noexport ml-auto flex items-center gap-1.5 shrink-0">
            <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={downloadJpeg} disabled={!!exporting} data-testid="sow-strip-jpg">
              {exporting === "jpg" ? <Loader2 className="h-3.5 w-3.5 sm:mr-1 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5 sm:mr-1" />}<span className="hidden sm:inline">JPEG</span>
            </Button>
            <Button type="button" size="sm" className="h-7 px-2 text-xs" onClick={downloadPdf} disabled={!!exporting} data-testid="sow-strip-pdf">
              {exporting === "pdf" ? <Loader2 className="h-3.5 w-3.5 sm:mr-1 animate-spin" /> : <FileDown className="h-3.5 w-3.5 sm:mr-1" />}<span className="hidden sm:inline">Afiş PDF</span>
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
          {data.winners.map((w) => {
            const a = accentFor(w.grade);
            return (
              <div key={w.grade} className="flex items-center gap-3 rounded-xl bg-card/70 border border-border px-3 py-2.5" data-testid={`sow-strip-${w.grade}`}>
                <div className={`h-12 w-12 rounded-full ring-2 ${a.ring} bg-muted overflow-hidden grid place-items-center shrink-0`}>
                  {w.photo_url
                    ? <img src={w.photo_url} alt={w.student_name} className="h-full w-full object-cover" />
                    : <UserRound className="h-6 w-6 text-muted-foreground" />}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${a.soft} ${a.text}`}>{w.class}</span>
                    <span className="text-[11px] font-bold text-amber-600 dark:text-amber-400">{w.score}</span>
                  </div>
                  <div className="text-xs font-semibold truncate mt-0.5" title={w.student_name}>{w.student_name}</div>
                </div>
              </div>
            );
          })}
        </div>
        {data.quote && <p className="mt-3 text-center text-[11px] text-amber-700 dark:text-amber-300 italic">“{data.quote}”</p>}
      </div>
    );
  }

  if (!data.exam || data.winners.length === 0) {
    return (
      <Card className="p-8 bg-card border-border text-center" data-testid="sow-empty">
        <Trophy className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
        <h3 className="font-semibold text-lg">Henüz haftanın öğrencisi yok</h3>
        <p className="text-sm text-muted-foreground mt-1">Bir KTT sonuçlandırıldığında, her sınıf seviyesinden en yüksek puan ve neti alan öğrenci burada otomatik görünür.</p>
      </Card>
    );
  }

  return (
    <div data-testid="sow-section">
      <div className="relative mb-5 rounded-2xl overflow-hidden border border-amber-500/30 shadow-sm">
        <img src="/champions_bg.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/75 to-slate-900/55" />
        <div className="relative flex items-center gap-3 p-4">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 grid place-items-center shadow shrink-0">
            <Trophy className="h-6 w-6 text-white" />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-lg leading-tight text-white">Haftanın Öğrencileri</h3>
            <p className="text-xs text-slate-200 mt-0.5">
              <span className="font-semibold text-amber-300">{data.exam.name}</span>
              {data.exam.date ? ` · ${data.exam.date}` : ""} · her sınıf seviyesinin en yüksek puan + neti
            </p>
          </div>
          <div className="sow-noexport ml-auto flex items-center gap-2 shrink-0">
            <Button type="button" variant="outline" size="sm" className="bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white" onClick={downloadJpeg} disabled={!!exporting} data-testid="sow-poster-jpg">
              {exporting === "jpg" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ImageIcon className="h-4 w-4 mr-1.5" />}Afiş (JPEG)
            </Button>
            <Button type="button" size="sm" onClick={downloadPdf} disabled={!!exporting} data-testid="sow-poster-pdf">
              {exporting === "pdf" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />}Afiş (PDF)
            </Button>
          </div>
        </div>
      </div>

      {examList.length > 1 && (
        <div className="flex items-center gap-2 mb-5" data-testid="sow-exam-select-wrap">
          <span className="text-sm font-medium text-muted-foreground shrink-0">KTT seç:</span>
          <Select value={selExam || data.exam?.id || ""} onValueChange={(v) => setSelExam(v)}>
            <SelectTrigger className="w-full sm:w-[260px]" data-testid="sow-exam-select">
              <SelectValue placeholder="KTT seçin" />
            </SelectTrigger>
            <SelectContent>
              {examList.map((e) => (
                <SelectItem key={e.id} value={e.id} data-testid={`sow-exam-opt-${e.id}`}>
                  {e.name}{e.date ? ` · ${e.date}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        {data.winners.map((w) => {
          const a = accentFor(w.grade);
          return (
            <Card key={w.grade} className="relative overflow-hidden border-border bg-card pb-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`sow-card-${w.grade}`}>
              <div className={`h-24 bg-gradient-to-br ${a.grad} relative`}>
                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/25 backdrop-blur-sm text-white text-xs font-bold tracking-wide">
                  {w.grade}. Sınıf
                </div>
                <Star className="absolute top-3 right-3 h-5 w-5 text-white/80 fill-white/60" />
                <div className="absolute -bottom-10 left-1/2 -translate-x-1/2">
                  <div className={`h-20 w-20 rounded-full ring-4 ${a.ring} ring-offset-2 ring-offset-card bg-muted overflow-hidden grid place-items-center shadow-lg`}>
                    {w.photo_url
                      ? <img src={w.photo_url} alt={w.student_name} className="h-full w-full object-cover" data-testid={`sow-photo-img-${w.grade}`} />
                      : <UserRound className="h-9 w-9 text-muted-foreground" />}
                    {busy === w.grade && <div className="absolute inset-0 bg-black/40 grid place-items-center"><Loader2 className="h-5 w-5 text-white animate-spin" /></div>}
                  </div>
                </div>
              </div>

              <div className="pt-12 px-4 text-center">
                <div className="font-bold text-[15px] leading-tight truncate" title={w.student_name}>{w.student_name || "—"}</div>
                <div className="mt-1 inline-flex items-center gap-1.5">
                  <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${a.soft} ${a.text}`}>{w.class}</span>
                  {w.number ? <span className="text-xs text-muted-foreground">No {w.number}</span> : null}
                </div>

                <div className="grid grid-cols-2 gap-2 mt-4">
                  <div className="rounded-lg bg-muted/60 py-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Puan</div>
                    <div className={`text-xl font-extrabold ${a.text}`} data-testid={`sow-score-${w.grade}`}>{w.score}</div>
                  </div>
                  <div className="rounded-lg bg-muted/60 py-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Net</div>
                    <div className="text-xl font-extrabold text-foreground">{w.total_net}</div>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] font-medium text-amber-600 dark:text-amber-400">
                  <Trophy className="h-3.5 w-3.5" /> Bu haftanın şampiyonu
                </div>

                {isAdmin && (
                  <div className="sow-admin-ctl mt-3 flex items-center justify-center gap-1.5">
                    <input type="file" accept="image/*" className="hidden"
                      ref={(el) => (inputs.current[`u${w.grade}`] = el)}
                      onChange={(e) => onPhoto(w, e)} data-testid={`sow-upload-input-${w.grade}`} />
                    <input type="file" accept="image/*" capture="user" className="hidden"
                      ref={(el) => (inputs.current[`c${w.grade}`] = el)}
                      onChange={(e) => onPhoto(w, e)} data-testid={`sow-cam-input-${w.grade}`} />
                    <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-xs"
                      onClick={() => inputs.current[`u${w.grade}`]?.click()} data-testid={`sow-upload-${w.grade}`}>
                      <Upload className="h-3.5 w-3.5 mr-1" />Yükle
                    </Button>
                    <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-xs"
                      onClick={() => inputs.current[`c${w.grade}`]?.click()} data-testid={`sow-cam-${w.grade}`}>
                      <Camera className="h-3.5 w-3.5 mr-1" />Çek
                    </Button>
                    {w.photo_url && (
                      <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs text-destructive"
                        onClick={() => removePhoto(w)} data-testid={`sow-remove-${w.grade}`}>Sil</Button>
                    )}
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {data.quote && (
        <div className="relative mt-5 rounded-2xl overflow-hidden border border-amber-500/30 shadow-sm" data-testid="sow-quote">
          <img src="/champions_bg.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/60" />
          <div className="relative flex items-start gap-3 p-4">
            <Quote className="h-6 w-6 text-amber-300 shrink-0 mt-0.5" />
            <p className="text-white font-semibold text-sm sm:text-base leading-snug">{data.quote}</p>
          </div>
        </div>
      )}

      {history && history.length > 1 && (
        <div className="mt-8" data-testid="sow-history">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-slate-500 to-slate-700 grid place-items-center shadow-sm shrink-0">
              <History className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-lg leading-tight">Geçmiş Haftalar</h3>
              <p className="text-xs text-muted-foreground">Her KTT'nin şampiyonları ve bir önceki sınava göre net/puan değişimi</p>
            </div>
            <Button type="button" size="sm" className="ml-auto shrink-0" onClick={downloadHistoryPdf} disabled={!!exporting} data-testid="sow-history-pdf">
              {exporting === "hist" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />}Tüm Şampiyonlar (PDF)
            </Button>
          </div>

          <div className="relative space-y-6 before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-px before:bg-border">
            {history.map((wk) => (
              <div key={wk.exam.id} className="relative pl-8" data-testid={`sow-week-${wk.exam.id}`}>
                <div className="absolute left-0 top-1.5 h-[23px] w-[23px] rounded-full bg-background border-2 border-amber-400 grid place-items-center">
                  <Trophy className="h-3 w-3 text-amber-500" />
                </div>
                <div className="flex items-baseline gap-2 flex-wrap mb-2.5">
                  <span className="font-bold text-sm">{wk.exam.name}</span>
                  {wk.exam.date && <span className="text-xs text-muted-foreground">{wk.exam.date}</span>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                  {wk.winners.map((w) => {
                    const a = accentFor(w.grade);
                    return (
                      <div key={w.grade} className="flex items-start gap-3 rounded-xl border border-border bg-card px-3 py-3 shadow-sm" data-testid={`sow-week-${wk.exam.id}-${w.grade}`}>
                        <div className={`h-12 w-12 rounded-full ring-2 ${a.ring} bg-muted overflow-hidden grid place-items-center shrink-0`}>
                          {w.photo_url
                            ? <img src={w.photo_url} alt={w.student_name} className="h-full w-full object-cover" />
                            : <UserRound className="h-6 w-6 text-muted-foreground" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${a.soft} ${a.text}`}>{w.class}</span>
                            <span className="text-[11px] font-bold text-amber-600 dark:text-amber-400">{w.score} puan</span>
                          </div>
                          <div className="text-xs font-semibold truncate mt-1" title={w.student_name}>{w.student_name}</div>
                          <div className="text-[10px] text-muted-foreground mt-0.5">Net {w.total_net}</div>
                          <div className="flex flex-wrap items-center gap-1 mt-1.5">
                            <DeltaBadge value={w.net_delta} label="net" />
                            <DeltaBadge value={w.score_delta} label="puan" />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
